import { LiquidEther } from "@/components/landing/liquid-ether";
import { MissionPhotoStage } from "@/components/mission-chrome";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, FileText, LayoutGrid, Shield, Users } from "lucide-react";
import { useLayoutEffect, useRef, type RefObject } from "react";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

/** v1 ship slice — Spec · Design · Verify · Review (chrome pipeline, not Guide/Setup/Track). */
const PREFLIGHT = [
  { label: "Spec", Icon: FileText },
  { label: "Design", Icon: LayoutGrid },
  { label: "Verify", Icon: Shield },
  { label: "Review", Icon: Users },
] as const;

export function LandingHero({ scrollerRef }: { scrollerRef: RefObject<HTMLDivElement | null> }) {
  const heroRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const hero = heroRef.current;
    const scroller = scrollerRef.current;
    if (!hero || !scroller) return;

    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const copy = hero.querySelectorAll("[data-hero-copy]");
        gsap.fromTo(
          copy,
          { y: 28, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 0.72, stagger: 0.09, ease: "power3.out" },
        );
        const photo = hero.querySelector(".mission-photo-img");
        if (photo) {
          gsap.fromTo(
            photo,
            { scale: 1.08 },
            {
              scale: 1,
              ease: "none",
              scrollTrigger: {
                trigger: hero,
                scroller,
                start: "top top",
                end: "bottom top",
                scrub: 0.65,
              },
            },
          );
        }
      });
    }, hero);

    const onLoad = () => ScrollTrigger.refresh();
    const img = hero.querySelector<HTMLImageElement>(".mission-photo-img");
    img?.addEventListener("load", onLoad);

    return () => {
      img?.removeEventListener("load", onLoad);
      ctx.revert();
    };
  }, [scrollerRef]);

  return (
    <section
      ref={heroRef}
      data-landing-section="hero"
      className="mission-hero relative -mt-[72px] flex min-h-full flex-col pt-[72px]"
    >
      <MissionPhotoStage veil="landing" contained />
      <LiquidEther />
      <div className="relative z-[2] flex min-h-0 flex-1 flex-col px-6 pb-4 sm:px-10">
        <div className="flex max-w-[640px] flex-1 flex-col justify-center py-10 sm:py-16">
          <p
            data-hero-copy
            className="mb-[18px] text-[11px] font-semibold tracking-[0.16em] text-falcon uppercase"
          >
            pcbx.ai · rscircuit Team Platform
          </p>
          <h1
            data-hero-copy
            className="mb-[18px] text-[40px] leading-[1.05] font-bold tracking-[-0.02em] sm:text-[54px]"
            style={{ textShadow: "0 10px 48px rgba(0,0,0,0.65)" }}
          >
            Every board
            <br />
            is a Mission.
          </h1>
          <p data-hero-copy className="mb-7 max-w-[520px] text-[16px] leading-[1.55] text-white/74">
            Guided hardware from Spec to schematic — then firmware. Preflight checklists, design reviews, and Mission
            control so teams ship flight-ready boards, not PDF threads.
          </p>
          <div data-hero-copy className="flex flex-wrap items-center gap-3">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 rounded-full border border-falcon bg-falcon px-5 py-2.5 text-[12px] font-semibold tracking-[0.06em] text-white uppercase no-underline"
            >
              Begin Mission
              <ArrowRight className="size-3.5" aria-hidden />
            </Link>
            <a
              href="#stages"
              className="inline-flex items-center gap-2 rounded-full border border-white/28 px-5 py-2.5 text-[12px] font-semibold tracking-[0.06em] text-white uppercase no-underline"
            >
              See stages
              <ArrowRight className="size-3.5" aria-hidden />
            </a>
            <Link
              to="/privacy"
              rel="privacy-policy"
              className="text-[12px] tracking-[0.06em] text-white/70 no-underline hover:text-white"
            >
              Privacy Policy
            </Link>
          </div>
        </div>
        <div className="flex flex-nowrap items-center overflow-x-auto border-t border-white/12 pt-4 pb-1">
          <div className="mr-[18px] border-r border-white/12 pr-[18px] text-[10px] tracking-[0.14em] text-white/45 uppercase">
            Preflight
          </div>
          {PREFLIGHT.map(({ label, Icon }, i) => (
            <span key={label} className="flex items-center">
              {i > 0 ? <span className="px-0.5 text-[12px] text-white/30">·</span> : null}
              <span className="flex items-center gap-1.5 px-3 text-[12px] tracking-[0.04em] text-white/78">
                <Icon className="size-3.5 text-white/55" aria-hidden />
                {label}
              </span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
